import { Button, Container, Divider, Stack } from "@mui/material";
import deepEqual from "fast-deep-equal";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import { emailValidator, requiredValidator, validResult } from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../api/adminApi";
import type { InvestorPortalSettings } from "../../../../api/types/investorPortalTypes";
import useClientContext from "../../../../context/useClientContext";
import type { EmailTemplateSettings } from "../../../common/email-templates/EmailTemplateEditor";
import EmailTemplateEditor from "../../../common/email-templates/EmailTemplateEditor";
import AutomaticInvitesSection from "./AutomaticInvitesSection";
import SendTestInvitationEmail from "./SendTestInvitationEmail";

const snippets = [
  { value: "{{FundName}}", displayName: "Fund Name" },
  { value: "{{InvestorName}}", displayName: "Investor Name" },
  { value: "{{RecipientName}}", displayName: "Contact Name" },
];

interface SettingsForm {
  enableAutomaticInvites: boolean;
  subject: string;
  body: string;
  cc: string;
  bcc: string;
}

const updateInvestorPortalSettings = withErrorHandling(adminApi.updateInvestorPortalSettings);

const splitCommaSeparatedString = (str: string) =>
  str
    .split(",")
    .map((s) => sanitizeEmail(s))
    .filter(Boolean);

const validateCommaSeparatedEmailString = (str: string) => {
  const emails = splitCommaSeparatedString(str);
  for (const email of emails) {
    const result = emailValidator(email);
    if (!result.isValid) {
      return result;
    }
  }

  return validResult();
};

const validateSettings = (settings: SettingsForm) => {
  return {
    subject: requiredValidator(settings.subject),
    body: requiredValidator(settings.body),
    cc: validateCommaSeparatedEmailString(settings.cc),
    bcc: validateCommaSeparatedEmailString(settings.bcc),
  };
};

const initFromSavedSettings = (savedSettings: InvestorPortalSettings): SettingsForm => ({
  enableAutomaticInvites: savedSettings.enableAutomaticInvites,
  subject: savedSettings.contactInvitationEmailSubject,
  body: savedSettings.contactInvitationEmailMessageText,
  cc: savedSettings.contactInvitationEmailCC,
  bcc: savedSettings.contactInvitationEmailBCC,
});

const InvitationEmailSection = () => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { hasPermissions } = useClientContext();

  const [currentSettings, setCurrentSettings] = useState<SettingsForm>();
  const [isSaving, setIsSaving] = useState(false);

  const [savedSettings, fetchSettingsError, { setData: setSavedSettings }] = useFetch(
    adminApi.getInvestorPortalSettings,
    {
      onSetData: (data) => {
        setCurrentSettings(initFromSavedSettings(data.settings));
      },
    }
  );

  const hasPermissionsToEdit = useMemo(() => hasPermissions(["ManageInvestorPortalSettings"]), [hasPermissions]);

  const [testInvitationContactsResp, fetchTestContactsError, { isFetching: isFetchingTestContacts }] = useFetch(
    adminApi.getTestInvitationContacts,
    { disabled: !hasPermissionsToEdit }
  );

  const testInvitationContacts = testInvitationContactsResp?.contacts ?? [];

  const fetchError = (hasPermissionsToEdit ? fetchTestContactsError : undefined) || fetchSettingsError;
  if (fetchError) {
    logError(fetchError, "[InvitationEmailSection] fetch");
    return <DataLoadingFailed title="Failed to load settings" />;
  }

  if (savedSettings === undefined || !currentSettings || isFetchingTestContacts) {
    return <InlineLoader />;
  }

  const handleSave = async () => {
    const enableAutomaticInvites = currentSettings.enableAutomaticInvites;
    const contactInvitationEmailSubject = currentSettings.subject.trim();
    const contactInvitationEmailMessageText = currentSettings.body.trim();
    const contactInvitationEmailCC = splitCommaSeparatedString(currentSettings.cc).join(",");
    const contactInvitationEmailBCC = splitCommaSeparatedString(currentSettings.bcc).join(",");

    setIsSaving(true);

    const [resp, error] = await updateInvestorPortalSettings({
      enableAutomaticInvites,
      contactInvitationEmailSubject,
      contactInvitationEmailMessageText,
      contactInvitationEmailCC,
      contactInvitationEmailBCC,
    });

    setIsSaving(false);

    if (error) {
      logError(error, "[InvitationEmailEditor]");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Invitation email settings saved");
    setSavedSettings(resp);
    setCurrentSettings(initFromSavedSettings(resp.settings));
  };

  const handleReset = () => {
    setCurrentSettings(initFromSavedSettings(savedSettings.settings));
  };

  const handleEmailSettingsChange = (update: Partial<EmailTemplateSettings>) =>
    setCurrentSettings((prev) =>
      prev
        ? {
            ...prev,
            ...update,
          }
        : undefined
    );

  const handleAutomaticInvitesChange = (checked: boolean) => {
    setCurrentSettings((prev) =>
      prev
        ? {
            ...prev,
            enableAutomaticInvites: checked,
          }
        : undefined
    );
  };

  const isDirty =
    !!savedSettings &&
    !deepEqual(
      {
        enableAutomaticInvites: savedSettings.settings.enableAutomaticInvites,
        subject: savedSettings.settings.contactInvitationEmailSubject,
        body: savedSettings.settings.contactInvitationEmailMessageText,
        cc: savedSettings.settings.contactInvitationEmailCC,
        bcc: savedSettings.settings.contactInvitationEmailBCC,
      },
      currentSettings
    );

  const settingsValidationResults = validateSettings(currentSettings);

  const areSettingsValid = Object.values(settingsValidationResults).every((result) => result.isValid);

  return (
    <Container maxWidth="md" disableGutters>
      <Stack spacing={3} sx={{ py: 2.5 }}>
        <AutomaticInvitesSection
          enabled={currentSettings.enableAutomaticInvites}
          onChange={handleAutomaticInvitesChange}
          disabled={!hasPermissionsToEdit}
        />

        <Stack spacing={3}>
          <EmailTemplateEditor
            snippets={snippets}
            settings={currentSettings}
            onChange={handleEmailSettingsChange}
            disabled={!hasPermissionsToEdit}
            validationResults={isDirty ? settingsValidationResults : {}}
          />

          {hasPermissionsToEdit && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                disabled={!isDirty || !areSettingsValid}
                loading={isSaving}
                onClick={handleSave}
              >
                Save
              </Button>
              <Button variant="text" color="secondary" disabled={!isDirty} onClick={handleReset}>
                Cancel
              </Button>
            </Stack>
          )}
        </Stack>

        {hasPermissionsToEdit && testInvitationContacts.length > 0 && (
          <>
            <Divider />
            <SendTestInvitationEmail testInvitationContacts={testInvitationContacts} disabled={isDirty} />
          </>
        )}
      </Stack>
    </Container>
  );
};

export default InvitationEmailSection;
